/**
 * Verdict → visual tone mapping (F6-6).
 *
 * Maps each enum value to a semantic *tone*, and each tone to design-token
 * Tailwind classes. No hardcoded colours — every class resolves to a token in
 * web/src/design-tokens/global.ts (semantic.* / ink.*). The class strings are
 * full literals so Tailwind's content scanner keeps them.
 *
 * F6 has no dedicated palette by design: pass→success, warning→warning,
 * fail→danger, unverifiable / n/a / unknown → neutral ink.
 */
import type {
  VerdictConsistency,
  VerdictExistence,
  VerdictOverall,
  VerdictValidity,
} from '../../lib/veridoc-api';

export type Tone = 'success' | 'warning' | 'danger' | 'neutral';

interface ToneClasses {
  /** Solid-ish badge (overall status). */
  badge: string;
  /** Small inline pill (dimension chips). */
  pill: string;
  /** Status dot. */
  dot: string;
}

export const TONE_CLASSES: Record<Tone, ToneClasses> = {
  success: {
    badge: 'bg-semantic-successBg text-semantic-success border-semantic-success',
    pill: 'bg-semantic-successBg text-semantic-success',
    dot: 'bg-semantic-success',
  },
  warning: {
    badge: 'bg-semantic-warningBg text-semantic-warning border-semantic-warning',
    pill: 'bg-semantic-warningBg text-semantic-warning',
    dot: 'bg-semantic-warning',
  },
  danger: {
    badge: 'bg-semantic-dangerBg text-semantic-danger border-semantic-danger',
    pill: 'bg-semantic-dangerBg text-semantic-danger',
    dot: 'bg-semantic-danger',
  },
  neutral: {
    badge: 'bg-ink-100 text-ink-500 border-ink-300',
    pill: 'bg-ink-100 text-ink-500',
    dot: 'bg-ink-400',
  },
};

export const OVERALL_TONE: Record<VerdictOverall, Tone> = {
  pass: 'success',
  warning: 'warning',
  fail: 'danger',
  unverifiable: 'neutral',
};

export const EXISTENCE_TONE: Record<VerdictExistence, Tone> = {
  existing: 'success',
  'not-found': 'danger',
  unverifiable: 'neutral',
};

export const VALIDITY_TONE: Record<VerdictValidity, Tone> = {
  'in-force': 'success',
  repealed: 'danger',
  amended: 'warning',
  unknown: 'neutral',
  'n/a': 'neutral',
};

export const CONSISTENCY_TONE: Record<VerdictConsistency, Tone> = {
  consistent: 'success',
  questionable: 'warning',
  inconsistent: 'danger',
  'not-evaluated': 'neutral',
};
